// Recurring expenses management hooks
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { queryKeys } from '@/lib/query-client'
import { isAssistantLoggedIn } from '@/lib/permissions'
import type { RecurringExpense } from '@/types/entities'

// Extended RecurringExpense type with audit display fields
export interface RecurringExpenseWithAudit extends RecurringExpense {
  deletedAt?: string
  createdBy?: string
  createdByAssistantId?: string
  updatedBy?: string
  updatedByAssistantId?: string
  createdByDisplay?: string
  updatedByDisplay?: string
}

/**
 * Fetch all recurring expenses for a restaurant
 * Automatically uses assistant RPC if logged in as assistant
 */
export function useRecurringExpenses(restaurantId: string | null, includeDeleted = false) {
  return useQuery({
    queryKey: [...queryKeys.accounting.recurringExpenses(restaurantId || ''), { includeDeleted }],
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    queryFn: async (): Promise<RecurringExpenseWithAudit[]> => {
      if (!restaurantId) {
        return []
      }

      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        const { data, error } = await supabase.rpc('assistant_get_recurring_expenses' as any, {
          p_restaurant_id: restaurantId,
          p_include_deleted: includeDeleted,
        })

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            console.warn('Assistant does not have permission to view recurring expenses')
            return []
          }
          throw error
        }

        return (data || []).map((re: any) => ({
          id: re.id,
          restaurantId: re.restaurant_id,
          categoryId: re.category_id,
          nameAr: re.name_ar,
          nameFr: re.name_fr,
          amount: Number(re.amount),
          isActive: re.is_active,
          notes: re.notes,
          startDate: re.start_date || re.created_at,
          createdAt: re.created_at,
          updatedAt: re.updated_at,
          deletedAt: re.deleted_at,
          createdBy: re.created_by,
          createdByAssistantId: re.created_by_assistant_id,
          updatedBy: re.updated_by,
          updatedByAssistantId: re.updated_by_assistant_id,
          categoryNameAr: re.category_name_ar,
          categoryNameFr: re.category_name_fr,
          categoryIcon: re.category_icon,
          categoryColor: re.category_color,
          createdByDisplay: re.created_by_display,
          updatedByDisplay: re.updated_by_display,
        }))
      }

      // Admin flow
      const { data, error } = await supabase.rpc('get_recurring_expenses', {
        p_restaurant_id: restaurantId,
        p_include_deleted: includeDeleted,
      })

      if (error) {
        throw error
      }

      return (data || []).map((re: any) => ({
        id: re.id,
        restaurantId: re.restaurant_id,
        categoryId: re.category_id,
        nameAr: re.name_ar,
        nameFr: re.name_fr,
        amount: Number(re.amount),
        isActive: re.is_active,
        notes: re.notes,
        startDate: re.start_date || re.created_at,
        createdAt: re.created_at,
        updatedAt: re.updated_at,
        deletedAt: re.deleted_at,
        createdBy: re.created_by,
        createdByAssistantId: re.created_by_assistant_id,
        updatedBy: re.updated_by,
        updatedByAssistantId: re.updated_by_assistant_id,
        categoryNameAr: re.category_name_ar,
        categoryNameFr: re.category_name_fr,
        categoryIcon: re.category_icon,
        categoryColor: re.category_color,
        createdByDisplay: re.created_by_display,
        updatedByDisplay: re.updated_by_display,
      }))
    },
    enabled: !!restaurantId,
  })
}

/**
 * Create a new recurring expense
 * Automatically uses assistant RPC if logged in as assistant
 */
export function useCreateRecurringExpense(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      categoryId: string
      nameAr: string
      nameFr: string
      amount: number
      startDate: string
      notes?: string
    }) => {
      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        const { data, error } = await supabase.rpc('assistant_create_recurring_expense' as any, {
          p_restaurant_id: restaurantId,
          p_category_id: request.categoryId,
          p_name_ar: request.nameAr,
          p_name_fr: request.nameFr,
          p_amount: request.amount,
          p_start_date: request.startDate,
          p_notes: (request.notes || null) as any,
        })

        if (error) {
          throw error
        }

        return data
      }

      // Admin flow
      const { data, error } = await supabase.rpc('create_recurring_expense', {
        p_restaurant_id: restaurantId,
        p_category_id: request.categoryId,
        p_name_ar: request.nameAr,
        p_name_fr: request.nameFr,
        p_amount: request.amount,
        p_start_date: request.startDate,
        p_notes: (request.notes || null) as any,
      })

      if (error) {
        throw error
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.accounting.recurringExpenses(restaurantId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.accounting.recurringExpensesSummary(restaurantId),
      })
    },
  })
}

/**
 * Update an existing recurring expense
 * Automatically uses assistant RPC if logged in as assistant
 */
export function useUpdateRecurringExpense(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      recurringExpenseId: string
      categoryId: string
      nameAr: string
      nameFr: string
      amount: number
      startDate: string
      isActive: boolean
      notes?: string
    }) => {
      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        const { data, error } = await supabase.rpc('assistant_update_recurring_expense' as any, {
          p_restaurant_id: restaurantId,
          p_recurring_expense_id: request.recurringExpenseId,
          p_category_id: request.categoryId,
          p_name_ar: request.nameAr,
          p_name_fr: request.nameFr,
          p_amount: request.amount,
          p_start_date: request.startDate,
          p_is_active: request.isActive,
          p_notes: (request.notes || null) as any,
        })

        if (error) {
          throw error
        }

        return data
      }

      // Admin flow
      const { data, error } = await supabase.rpc('update_recurring_expense', {
        p_recurring_expense_id: request.recurringExpenseId,
        p_category_id: request.categoryId,
        p_name_ar: request.nameAr,
        p_name_fr: request.nameFr,
        p_amount: request.amount,
        p_start_date: request.startDate,
        p_is_active: request.isActive,
        p_notes: (request.notes || null) as any,
      })

      if (error) {
        throw error
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.accounting.recurringExpenses(restaurantId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.accounting.recurringExpensesSummary(restaurantId),
      })
    },
  })
}

/**
 * Delete a recurring expense
 * Automatically uses assistant RPC if logged in as assistant
 */
export function useDeleteRecurringExpense(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (recurringExpenseId: string) => {
      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        const { data, error } = await supabase.rpc('assistant_delete_recurring_expense' as any, {
          p_restaurant_id: restaurantId,
          p_recurring_expense_id: recurringExpenseId,
        })

        if (error) {
          throw error
        }

        return data
      }

      // Admin flow
      const { data, error } = await supabase.rpc('delete_recurring_expense', {
        p_recurring_expense_id: recurringExpenseId,
      })

      if (error) {
        throw error
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.accounting.recurringExpenses(restaurantId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.accounting.recurringExpensesSummary(restaurantId),
      })
    },
  })
}

/**
 * Pay a recurring expense (create expense from recurring)
 * Automatically uses assistant RPC if logged in as assistant
 */
export function usePayRecurringExpense(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      recurringExpenseId: string
      expenseDate: string
      amount?: number
      notes?: string
    }) => {
      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        const { data, error } = await supabase.rpc('assistant_pay_recurring_expense' as any, {
          p_restaurant_id: restaurantId,
          p_recurring_expense_id: request.recurringExpenseId,
          p_expense_date: request.expenseDate,
          p_amount: (request.amount || null) as any,
          p_notes: (request.notes || null) as any,
        })

        if (error) {
          throw error
        }

        return data
      }

      // Admin flow - use create_expense with recurring_expense_id
      // First get the recurring expense details
      const { data: recurringExpenses } = await supabase.rpc('get_recurring_expenses', {
        p_restaurant_id: restaurantId,
      })

      const recurring = recurringExpenses?.find((re: any) => re.id === request.recurringExpenseId)
      if (!recurring) {
        throw new Error('Recurring expense not found')
      }

      const { data, error } = await supabase.rpc('create_expense', {
        p_restaurant_id: restaurantId,
        p_category_id: recurring.category_id,
        p_expense_date: request.expenseDate,
        p_amount: request.amount ?? recurring.amount,
        p_description_ar: recurring.name_ar,
        p_description_fr: recurring.name_fr,
        p_recurring_expense_id: request.recurringExpenseId,
        p_notes: (request.notes || null) as any,
      })

      if (error) {
        throw error
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['accounting', 'expenses', restaurantId],
      })
      queryClient.invalidateQueries({
        queryKey: ['accounting', 'financial-summary', restaurantId],
      })
      queryClient.invalidateQueries({
        queryKey: ['accounting', 'expense-breakdown', restaurantId],
      })
    },
  })
}

/**
 * Fetch recurring expense edit history
 */
export function useRecurringExpenseEditHistory(restaurantId: string | null, recurringExpenseId: string | null) {
  return useQuery({
    queryKey: ['accounting', 'recurring-expense-edit-history', restaurantId, recurringExpenseId],
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    queryFn: async () => {
      if (!restaurantId || !recurringExpenseId) {
        return []
      }

      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        const { data, error } = await supabase.rpc('assistant_get_recurring_expense_edit_history' as any, {
          p_restaurant_id: restaurantId,
          p_recurring_expense_id: recurringExpenseId,
        })

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            console.warn('Assistant does not have permission to view recurring expense edit history')
            return []
          }
          throw error
        }

        return (data || []).map((h: any) => ({
          id: h.id,
          recurringExpenseId: h.recurring_expense_id,
          restaurantId: h.restaurant_id,
          fieldChanged: h.field_changed,
          oldValue: h.old_value,
          newValue: h.new_value,
          editedAt: h.edited_at,
          editedBy: h.edited_by,
          editedByAssistantId: h.edited_by_assistant_id,
          createdAt: h.created_at,
          modifiedBy: h.modified_by,
        }))
      }

      // Admin flow
      const { data, error } = await supabase.rpc('get_recurring_expense_edit_history' as any, {
        p_recurring_expense_id: recurringExpenseId,
      })

      if (error) {
        throw error
      }

      return ((data as any[]) || []).map((h: any) => ({
        id: h.id,
        recurringExpenseId: h.recurring_expense_id,
        restaurantId: h.restaurant_id,
        fieldChanged: h.field_changed,
        oldValue: h.old_value,
        newValue: h.new_value,
        editedAt: h.edited_at,
        editedBy: h.edited_by,
        editedByAssistantId: h.edited_by_assistant_id,
        createdAt: h.created_at,
        modifiedBy: h.modified_by,
      }))
    },
    enabled: !!restaurantId && !!recurringExpenseId,
  })
}
